import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, Pill, SectionLabel } from "@/components/common";
import { FlaskConical, Plus, Trash2, Power, Trophy } from "lucide-react";
import { UpgradeGate } from "@/components/app/UpgradeGate";

const STRATEGIES = [
  { name: "Trend Pullback", flagship: true, regime: "Trend", desc: "Identify an established trend and wait for price to return to a logical value zone with confirmation before entry.",
    rules: ["4H & 1H trend aligned", "Price at support/EMA/swing zone", "5M confirmation (rejection / structure shift)", "Min R:R 1.5"] },
  { name: "Trend Following", regime: "Trend", desc: "Trade in the direction of a strong, clean trend without chasing extension.",
    rules: ["Higher highs & higher lows", "EMA stack aligned", "ADX ≥ 25", "No excessive extension from mean"] },
  { name: "Breakout + Retest", regime: "Breakout / Compression", desc: "Wait for a confirmed break of a key level, then enter on the retest rather than chasing the breakout candle.",
    rules: ["Clear level break with displacement", "ATR expansion", "Retest holds", "Higher-timeframe support"] },
  { name: "Range Trading", regime: "Range", desc: "Fade range extremes with confirmation; avoid the middle of the range.",
    rules: ["Defined range high/low", "Price near extreme", "Confirmation candle", "Deactivate on strong breakout"] },
  { name: "Mean Reversion", regime: "Range", desc: "Trade statistical extension back toward the mean — only when regime supports it.",
    rules: ["RSI / Bollinger extension", "Regime = Range", "Overbought ≠ auto-sell", "Oversold ≠ auto-buy"] },
];

export default function StrategyLab() {
  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center gap-3"><FlaskConical className="text-aurum" /><h1 className="text-2xl font-bold tracking-tight">Strategy Lab</h1></div>
      <Card className="p-4 flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[var(--text-sec)]">Aurum monitors Gold against these strategy templates. The strategy router activates the right one for the current market regime.</p>
      </Card>
      <UpgradeGate feature="custom_strategies" title="Custom Strategy Builder"
        blurb="Define your own rule sets and let Aurum monitor Gold against them 24/7, alerting you the moment all conditions align."
        requiredPlan="Pro">
        <MyStrategies />
      </UpgradeGate>
      <SectionLabel>Built-in templates</SectionLabel>
      <div className="grid md:grid-cols-2 gap-5">
        {STRATEGIES.map((s) => (
          <Card key={s.name} className="p-5" data-testid={`strategy-${s.name.replace(/\s|\+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="font-semibold">{s.name}</span>
              {s.flagship ? <Pill color="#D6AE57">Flagship</Pill> : <Pill color="#60A5FA">{s.regime}</Pill>}
            </div>
            <p className="mt-2 text-sm text-[var(--text-sec)]">{s.desc}</p>
            <div className="mt-3"><SectionLabel>Entry rules</SectionLabel>
              <ul className="mt-2 space-y-1">
                {s.rules.map((r) => <li key={r} className="text-xs text-[var(--text-muted)]">• {r}</li>)}
              </ul>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const REGIMES = ["UPTREND", "DOWNTREND", "RANGE", "BREAKOUT", "COMPRESSION"];
const SESSIONS = ["Asian", "London", "New York"];

function MyStrategies() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [board, setBoard] = useState(null);
  const [ranking, setRanking] = useState(false);

  const rank = async () => {
    setRanking(true);
    try { const { data } = await api.get("/strategies/leaderboard"); setBoard(data.leaderboard || []); }
    catch { toast.error("Could not rank strategies."); }
    finally { setRanking(false); }
  };
  const activateWinner = async () => {
    try {
      const { data } = await api.post("/strategies/activate-top");
      toast.success(`Activated winner: ${data.activated} (${data.metric.return_pct}% forward).`);
      load(); rank();
    } catch (e) { toast.error(e.response?.data?.detail || "Run the leaderboard first."); }
  };

  const load = async () => {
    const { data } = await api.get("/strategies");
    setItems(data.strategies || []);
  };
  useEffect(() => { load(); }, []);

  const blank = { name: "", rules: { regime_filter: [], direction: "both", min_setup_score: 60, min_rr: 2, session_filter: [], news_filter: true }, active: true };

  const [presets, setPresets] = useState([]);
  useEffect(() => { api.get("/strategies/presets").then(({ data }) => setPresets(data.presets || [])).catch(() => {}); }, []);
  const activatePreset = async (preset) => {
    try {
      await api.post("/strategies", { ...preset, active: true });
      toast.success(`"${preset.name}" activated — Aurum is monitoring Gold for it.`);
      load();
    } catch { toast.error("Could not activate preset."); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name your strategy.");
    try {
      if (form.id) await api.put(`/strategies/${form.id}`, form);
      else await api.post("/strategies", form);
      toast.success("Strategy saved. Aurum is now monitoring Gold against it.");
      setForm(null); load();
    } catch { toast.error("Could not save."); }
  };
  const toggleActive = async (s) => { await api.put(`/strategies/${s.id}`, { ...s, active: !s.active }); load(); };
  const del = async (id) => { await api.delete(`/strategies/${id}`); load(); };

  const toggleArr = (key, val) => {
    const arr = form.rules[key];
    const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
    setForm({ ...form, rules: { ...form.rules, [key]: next } });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <SectionLabel>My Strategies — Aurum monitors Gold against your rules</SectionLabel>
        <div className="flex items-center gap-2">
          <button onClick={rank} disabled={ranking || items.length === 0} data-testid="rank-strategies"
            className="flex items-center gap-1.5 rounded-full border border-[var(--border2)] px-3 py-1.5 text-xs font-semibold hover:border-aurum hover:text-aurum transition-colors disabled:opacity-50">
            <Trophy size={13} /> {ranking ? "Ranking…" : "Leaderboard"}
          </button>
          <button onClick={() => setForm(blank)} data-testid="new-strategy" className="btn-gold flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold hover:brightness-110"><Plus size={14} /> New strategy</button>
        </div>
      </div>

      {board && (
        <Card className="mt-3 p-5" data-testid="leaderboard">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Trophy size={15} className="text-aurum" /><SectionLabel>Walk-Forward Leaderboard (out-of-sample forward return)</SectionLabel></div>
            <button onClick={activateWinner} data-testid="activate-winner" className="btn-gold rounded-full px-3 py-1.5 text-xs font-semibold hover:brightness-110">Auto-activate winner</button>
          </div>
          {board.length === 0 ? <div className="text-xs text-[var(--text-muted)]">No strategies to rank yet.</div> : (
            <div className="space-y-2">
              {board.map((e, i) => {
                const m = e.metric || {};
                const medal = ["🥇", "🥈", "🥉"][i] || `#${i + 1}`;
                const has = m.return_pct != null;
                return (
                  <div key={e.id} className="flex items-center justify-between rounded-lg border border-[var(--border2)] px-3 py-2.5" data-testid="leaderboard-row">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-7 text-center">{medal}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{e.name} {e.active && <Pill color="#21C58E">Active</Pill>}</div>
                        <div className="text-[11px] text-[var(--text-muted)] tnum">{has ? `${m.trades} trades · ${m.win_rate}% win · PF ${m.profit_factor}` : "no qualifying forward trades"}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`tnum text-sm font-semibold ${has ? (m.return_pct >= 0 ? "text-bull" : "text-bear") : "text-[var(--text-muted)]"}`}>{has ? `${m.return_pct}%` : "—"}</span>
                      {!e.active && <button onClick={() => { const full = items.find((x) => x.id === e.id); if (full) toggleActive(full); }} className="text-xs text-aurum hover:underline" data-testid="leaderboard-activate">Activate</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-3 text-[11px] text-[var(--text-muted)]">Ranked by forward (unseen) return from a walk-forward run on DEMO history. Past results do not guarantee future performance.</p>
        </Card>
      )}

      {presets.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2">One-click presets</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {presets.map((pr) => (
              <div key={pr.name} className="rounded-xl border border-[var(--border2)] bg-[var(--card2)] p-4 flex flex-col" data-testid="preset-card">
                <div className="text-sm font-medium">{pr.name}</div>
                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                  <Pill color="#60A5FA">{pr.timeframe}</Pill>
                  <Pill color="#98A2B3">{pr.rules.direction}</Pill>
                  <Pill color="#D6AE57">≥{pr.rules.min_setup_score}</Pill>
                  <Pill color="#D6AE57">R:R {pr.rules.min_rr}</Pill>
                </div>
                <button onClick={() => activatePreset(pr)} data-testid="activate-preset"
                  className="mt-3 rounded-lg border border-aurum text-aurum py-1.5 text-xs font-semibold hover:bg-[rgba(214,174,87,0.1)] transition-colors">Activate & monitor</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {items.length === 0 && !form && (
          <Card className="p-6 md:col-span-2 text-center text-sm text-[var(--text-muted)]">No custom strategies yet. Define your rules and Aurum will watch for them on the terminal.</Card>
        )}
        {items.map((s) => (
          <Card key={s.id} className="p-4" data-testid="my-strategy">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{s.name}</span>
              <div className="flex items-center gap-2">
                {s.active && <Pill color="#21C58E">Active</Pill>}
                <button onClick={() => toggleActive(s)} className="p-1.5 rounded-md hover:bg-[var(--surface)]" title="Toggle active"><Power size={14} className={s.active ? "text-bull" : "text-[var(--text-muted)]"} /></button>
                <button onClick={() => setForm(s)} className="text-xs text-aurum hover:underline">Edit</button>
                <button onClick={() => del(s.id)} className="p-1.5 rounded-md hover:bg-[var(--surface)]"><Trash2 size={14} className="text-bear" /></button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-[var(--text-muted)]">
              {(s.rules.regime_filter || []).map((r) => <Pill key={r} color="#60A5FA">{r}</Pill>)}
              <Pill color="#98A2B3">{s.rules.direction}</Pill>
              <Pill color="#D6AE57">score ≥ {s.rules.min_setup_score}</Pill>
              <Pill color="#D6AE57">R:R ≥ {s.rules.min_rr}</Pill>
              {s.rules.news_filter && <Pill color="#F5B942">news LOW</Pill>}
            </div>
          </Card>
        ))}
      </div>

      {form && (
        <Card className="mt-4 p-5 border-aurum" data-testid="strategy-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="text-xs text-[var(--text-sec)]">Strategy name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" data-testid="strategy-name" /></div>
            <div><label className="text-xs text-[var(--text-sec)]">Direction</label>
              <select value={form.rules.direction} onChange={(e) => setForm({ ...form, rules: { ...form.rules, direction: e.target.value } })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum">
                <option value="both">Both</option><option value="long">Long only</option><option value="short">Short only</option></select></div>
          </div>
          <div className="mt-4"><label className="text-xs text-[var(--text-sec)]">Allowed regimes</label>
            <div className="mt-1 flex flex-wrap gap-2">{REGIMES.map((r) => <button key={r} onClick={() => toggleArr("regime_filter", r)} className={`rounded-full px-3 py-1 text-xs border ${form.rules.regime_filter.includes(r) ? "border-aurum text-aurum" : "border-[var(--border2)] text-[var(--text-sec)]"}`}>{r}</button>)}</div></div>
          <div className="mt-4"><label className="text-xs text-[var(--text-sec)]">Sessions</label>
            <div className="mt-1 flex flex-wrap gap-2">{SESSIONS.map((r) => <button key={r} onClick={() => toggleArr("session_filter", r)} className={`rounded-full px-3 py-1 text-xs border ${form.rules.session_filter.includes(r) ? "border-aurum text-aurum" : "border-[var(--border2)] text-[var(--text-sec)]"}`}>{r}</button>)}</div></div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div><label className="text-xs text-[var(--text-sec)]">Min setup score</label>
              <input type="number" value={form.rules.min_setup_score} onChange={(e) => setForm({ ...form, rules: { ...form.rules, min_setup_score: Number(e.target.value) } })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" /></div>
            <div><label className="text-xs text-[var(--text-sec)]">Min R:R</label>
              <input type="number" step="0.1" value={form.rules.min_rr} onChange={(e) => setForm({ ...form, rules: { ...form.rules, min_rr: Number(e.target.value) } })} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" /></div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.rules.news_filter} onChange={(e) => setForm({ ...form, rules: { ...form.rules, news_filter: e.target.checked } })} className="h-4 w-4 accent-[#D6AE57]" /> Require LOW news environment
          </label>
          <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#D6AE57]" /> Set as active (Aurum monitors this)
          </label>
          <div className="mt-4 flex gap-2">
            <button onClick={save} data-testid="save-strategy" className="btn-gold rounded-lg px-5 py-2 text-sm font-semibold hover:brightness-110">Save strategy</button>
            <button onClick={() => setForm(null)} className="rounded-lg px-5 py-2 text-sm border border-[var(--border2)] hover:border-[var(--text-muted)]">Cancel</button>
          </div>
        </Card>
      )}
    </div>
  );
}
